import React, { useMemo, useState } from 'react'

import useTitles from 'common/hooks/useTitles'
import moment from 'moment'
import { round, uniq } from 'lodash'
import useUpdateEffect from 'common/hooks/useUpdateEffect'
import { getDate, getMonth, getSemester, getTrimester } from 'common/utils/DateUtil'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, Grid, Chip, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress'
import DownloadIcon from '@mui/icons-material/Download'
import { getColorAdv } from 'common/utils/ColorUtil'
import Select from 'common/components/form/Select'
import { CATEGORIES_TICKETS } from 'common/constants/Constants'
import BarChartIcon from '@mui/icons-material/BarChart'
import BugReportIcon from '@mui/icons-material/BugReport'
import BuildIcon from '@mui/icons-material/Build'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SchoolIcon from '@mui/icons-material/School'
import ComputerIcon from '@mui/icons-material/Computer'
import WorkIcon from '@mui/icons-material/Work'
import SettingsIcon from '@mui/icons-material/Settings'
import PropTypes from 'prop-types'
import DateTimePicker from 'common/components/form/DateTimePicker'
import { ExportAction } from 'common/utils/ExportUtils'
import { getTruncate, formatMilliers } from 'common/utils/StringUtil'
import useFetch from 'common/hooks/useFetch'
import { aquaFetch } from 'common/utils/ActionUtils'
import { STATUS } from 'common/constants/storeConstants'
import ApplicationConf from 'conf/ApplicationConf'
import { CardTable, NewTable, SORT, ALIGN } from 'common/components/datatable/Table'
import TimelineIcon from '@mui/icons-material/Timeline'
import TimesBar from './components/TimesBar'
import TicketsLine from './components/TicketsLine'
import LotsEvolutionDialog from './components/LotsEvolutionDialog'
import SupportEvolutionDialog from './components/SupportEvolutionDialog'
import IncidentsSupportApp from './components/IncidentsSupportApp'
import { computeSynthese } from './StatisticsUtil'
import TabList from 'common/components/tab/TabList'

// Produits (instanceType côté supervision) proposés comme raccourcis de remplissage des projets
const PRODUCTS = [
    { value: 'Myliaq', label: 'MyliaQ' },
    { value: 'Seqoya', label: 'SeQoya' },
    { value: 'Iryqua', label: 'IryQua' },
    { value: 'Acycliq', label: 'AcycliQ' },
]

const FilterStatistics = ({
    setFilter,
    defaultFilter,
}) => {
    const {
        data: projects = [],
    } = useFetch(ApplicationConf.support.projects())

    // Instances de l'accueil : permettent de déduire les projets de chaque produit (par instanceType)
    const {
        data: { instances = [] } = {},
    } = useFetch(ApplicationConf.dashboard())


    const [startDate, setStartDate] = useState(defaultFilter.startDate)
    const [endDate, setEndDate] = useState(defaultFilter.endDate)
    const [categories, setCategories] = useState(defaultFilter.categories)
    const [selectedProjects, setSelectedProjects] = useState(defaultFilter.projects)
    // Projets ajoutés par chaque module cliqué (pour pouvoir les retirer au reclic)
    const [productProjects, setProductProjects] = useState({})

    const filteredProjects = useMemo(() => {
        return projects.filter(item => !selectedProjects.includes(item.name))
    }, [projects, selectedProjects])

    const categoriesValues = [
        { key: CATEGORIES_TICKETS.ANALYSIS, label: CATEGORIES_TICKETS.ANALYSIS, Icon: BarChartIcon },
        { key: CATEGORIES_TICKETS.ANOMALY, label: CATEGORIES_TICKETS.ANOMALY, Icon: BugReportIcon },
        { key: CATEGORIES_TICKETS.SUPPORT, label: CATEGORIES_TICKETS.SUPPORT, Icon: BuildIcon },
        { key: CATEGORIES_TICKETS.EVOLUTION, label: CATEGORIES_TICKETS.EVOLUTION, Icon: TrendingUpIcon },
        { key: CATEGORIES_TICKETS.FORMATION, label: CATEGORIES_TICKETS.FORMATION, Icon: SchoolIcon },
        { key: CATEGORIES_TICKETS.INSTALLATION, label: CATEGORIES_TICKETS.INSTALLATION, Icon: ComputerIcon },
        { key: CATEGORIES_TICKETS.SERVICE, label: CATEGORIES_TICKETS.SERVICE, Icon: WorkIcon },
        { key: CATEGORIES_TICKETS.SYSTEM, label: CATEGORIES_TICKETS.SYSTEM, Icon: SettingsIcon },
    ]

    const onValidate = () => {
        setFilter({
            startDate,
            endDate,
            categories: (categories.length !== 0) ? categories : undefined,
            projects: (selectedProjects.length !== 0) ? selectedProjects : undefined,
        })
    }

    // Toggle des projets d'un module. Clic : ajoute les projets Mantis du module (déterminés par le
    // paramètre MANTIS_NAME de chaque instance = liste d'IDs, mappés vers les noms via `projects`).
    // Reclic : retire tous les projets qui avaient été ajoutés par ce module.
    const toggleProductProjects = (productValue) => {
        const alreadyAdded = productProjects[productValue]
        if (alreadyAdded) {
            setSelectedProjects(prev => prev.filter(p => !alreadyAdded.includes(p)))
            setProductProjects(prev => {
                const next = { ...prev }
                Reflect.deleteProperty(next, productValue)
                return next
            })
            return
        }
        const productInstances = instances.filter(i => i.instanceType === productValue)
        Promise.all(productInstances.map(i =>
            aquaFetch(ApplicationConf.instance.single(i.instanceId))
                .then(inst => inst?.parameters ?? [])
                .catch(() => []),
        )).then(paramsArrays => {
            const projectIds = paramsArrays
                .flat()
                .filter(p => p.parameter === 'MANTIS_NAME')
                .flatMap(p => (p.value ?? '').split(',').map(v => Number(v)))
                .filter(id => Number.isFinite(id))
            const names = projects.filter(p => projectIds.includes(p.id)).map(p => p.name)
            setSelectedProjects(prev => uniq([...prev, ...names]))
            setProductProjects(prev => ({ ...prev, [productValue]: names }))
        })
    }

    return (
        <Card sx={{ margin: '0 10px 10px 0' }}>
            <CardContent sx={{ paddingY: 1, '&:last-child': { paddingBottom: 1 } }}>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={2}>
                        <DateTimePicker label='Date de début' value={startDate} onChange={setStartDate}/>
                    </Grid>
                    <Grid size={2}>
                        <DateTimePicker label='Date de fin' value={endDate} onChange={setEndDate}/>
                    </Grid>
                    <Grid size={2}>
                        <Select
                            label='Projets'
                            items={filteredProjects}
                            onChange={(e) => {
                                const proj = projects.find(p => p.id === e)
                                setSelectedProjects(p => [...p, proj.name])
                            }}
                            sortKey='name'
                        />
                    </Grid>
                    <Grid size={6}>
                        {selectedProjects.length > 0 && (
                            <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                                <Stack direction='row' spacing={1}>
                                    {selectedProjects.map((project, index) => (
                                        <Chip
                                            key={index}
                                            label={project}
                                            size='small'
                                            onDelete={() => {
                                                setSelectedProjects((prevSelected) =>
                                                    prevSelected.filter((p) => p !== project),
                                                )
                                            }}
                                            sx={{
                                                color: '#00796b',
                                                backgroundColor: '#e0f7fa',
                                                borderRadius: '10px',
                                                border: 'solid 1px',
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Grid>
                    <Grid size={7}>
                        <ToggleButtonGroup
                            value={categories}
                            onChange={(_, newSelected) => setCategories(newSelected)}
                            aria-label='categories'
                            size='small'
                        >
                            {categoriesValues.map(({ key, label, Icon }) => (
                                <ToggleButton key={key} value={key} sx={{ paddingY: 0.25, paddingX: 1 }}>
                                    <Icon fontSize='small' sx={{ marginRight: 0.5 }} />
                                    {label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Grid>
                    <Grid size={3}>
                        <Stack direction='row' spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                            {PRODUCTS.map(p => (
                                <Button
                                    key={p.value}
                                    size='small'
                                    variant={productProjects[p.value] ? 'contained' : 'outlined'}
                                    onClick={() => toggleProductProjects(p.value)}
                                >
                                    {p.label}
                                </Button>
                            ))}
                        </Stack>
                    </Grid>
                    <Grid size={2} display='flex' justifyContent='flex-end'>
                        <Button
                            onClick={onValidate}
                            variant='contained'
                            color='primary'
                        >
                            {'Rechercher'}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterStatistics.propTypes = {
    setFilter: PropTypes.func,
    defaultFilter: PropTypes.shape({
        startDate: PropTypes.int,
        endDate: PropTypes.int,
        categories: PropTypes.arrayOf(
            PropTypes.oneOf(Object.values(CATEGORIES_TICKETS)),
        ),
        projects: PropTypes.arrayOf(
            PropTypes.string,
        ),
    }),
}

// Cellule de durée en jours ouvrés (réponse / résolution / MEP) : cellule rouge au-delà de 30
const tempsCell = (value) => ({
    value: Number.isFinite(value) ? formatMilliers(round(value, 1), true) : '',
    sortValue: Number.isFinite(value) ? value : -1,
    color: value > 30 ? 'red' : undefined,
    fontColor: value > 30 ? 'white' : undefined,
})

// Cellule date : affichée formatée mais triée sur le timestamp (unix secondes)
const dateCell = (timestamp) => ({
    value: Number.isFinite(timestamp) ? getDate(timestamp * 1000) : '',
    sortValue: Number.isFinite(timestamp) ? timestamp : -1,
})

// Format d'affichage des nombres : groupes de 3 chiffres (ex. 1 234)
const formattingTickets = list => list.map(row => ({
    id: row.id,
    idIssue: { value: row.id },
    summary: { value: getTruncate(row.summary), sortValue: row.summary },
    dateSubmitted: dateCell(row.dateSubmitted),
    tempsReponse: tempsCell(row.answerTime),
    dateResolution: dateCell(row.dateResolution),
    tempsResolution: tempsCell(row.solveTime),
    dateMep: dateCell(row.dateMep),
    tempsMep: tempsCell(row.mepTime),
    reporter: { value: row.reporter },
    handler: { value: row.handler },
    tempsPasse: { value: row.tempsPasse },
    lot: { value: row.lot },
    project: { value: row.project },
    status: { value: row.status },
}))

const headersTicketsTable = [
    { key: 'idIssue', value: 'Id' },
    { key: 'summary', value: 'Titre' },
    { key: 'dateSubmitted', value: 'Date de création' },
    { key: 'tempsReponse', value: 'TRep (jo)' },
    { key: 'dateResolution', value: 'Date de résolution' },
    { key: 'tempsResolution', value: 'TRes (jo)' },
    { key: 'dateMep', value: 'Date de MEP' },
    { key: 'tempsMep', value: 'TMep (jo)' },
    { key: 'reporter', value: 'Rapporteur' },
    { key: 'handler', value: 'Affecté à' },
    { key: 'tempsPasse', value: 'Temps passé' },
    { key: 'lot', value: 'Lot' },
    { key: 'project', value: 'Projet' },
    { key: 'status', value: 'Statut' },
]

const headersStockTable = [
    { key: 'idIssue', value: 'Id' },
    { key: 'summary', value: 'Titre' },
    { key: 'dateSubmitted', value: 'Date de création' },
    { key: 'tempsReponse', value: 'TRep (jo)' },
    { key: 'reporter', value: 'Rapporteur' },
    { key: 'handler', value: 'Affecté à' },
    { key: 'tempsPasse', value: 'Temps passé' },
    { key: 'lot', value: 'Lot' },
    { key: 'project', value: 'Projet' },
    { key: 'status', value: 'Statut' },
]

const headersTicketsExport = [...headersTicketsTable, { key: 'month', value: 'Mois' },
    { key: 'trimester', value: 'Trimestre' }, { key: 'semester', value: 'Semestre' }]

const headersStockExport = [...headersStockTable, { key: 'month', value: 'Mois' },
    { key: 'trimester', value: 'Trimestre' }, { key: 'semester', value: 'Semestre' }]

// Lignes d'export tickets (résolus / créés), même ordre que headersTicketsTable
const exportTicketRow = (item) => {
    const { dateSubmitted } = item
    const yearOfCreation = moment.unix(dateSubmitted).year()
    return [
        { value: String(item.id) },
        { value: item.summary ?? '' },
        { value: getDate(dateSubmitted * 1000) ?? '' },
        { value: Number.isFinite(item.answerTime) ? round(item.answerTime, 1) : '' },
        { value: item.dateResolution ? getDate(item.dateResolution * 1000) : '' },
        { value: Number.isFinite(item.solveTime) ? round(item.solveTime, 1) : '' },
        { value: item.dateMep ? getDate(item.dateMep * 1000) : '' },
        { value: Number.isFinite(item.mepTime) ? round(item.mepTime, 1) : '' },
        { value: item.reporter ?? '' },
        { value: item.handler ?? '' },
        { value: item.tempsPasse ?? '' },
        { value: item.lot ?? '' },
        { value: item.project ?? '' },
        { value: item.status ?? '' },
        { value: `${getMonth(dateSubmitted)} ${yearOfCreation}` },
        { value: `Trimestre ${getTrimester(dateSubmitted)} ${yearOfCreation}` },
        { value: `Semestre ${getSemester(dateSubmitted)} ${yearOfCreation}` },
    ]
}

// Lignes d'export stock, même ordre que headersStockTable
const exportStockRow = (item) => {
    const { dateSubmitted } = item
    const yearOfCreation = moment.unix(dateSubmitted).year()
    return [
        { value: String(item.id) },
        { value: item.summary ?? '' },
        { value: getDate(dateSubmitted * 1000) ?? '' },
        { value: Number.isFinite(item.answerTime) ? round(item.answerTime, 1) : '' },
        { value: item.reporter ?? '' },
        { value: item.handler ?? '' },
        { value: item.tempsPasse ?? '' },
        { value: item.lot ?? '' },
        { value: item.project ?? '' },
        { value: item.status ?? '' },
        { value: `${getMonth(dateSubmitted)} ${yearOfCreation}` },
        { value: `Trimestre ${getTrimester(dateSubmitted)} ${yearOfCreation}` },
        { value: `Semestre ${getSemester(dateSubmitted)} ${yearOfCreation}` },
    ]
}

const IssuesResolvedCard = ({
    filter,
    defaultExpanded,
}) => {
    const {
        data: result = [],
        reload: reload,
        status: status,
    } = useFetch(ApplicationConf.support.statisticsResolved(), { method: 'POST', body: filter })

    useUpdateEffect(() => {
        reload()
    }, [filter])

    const formattingExportData = (data) => [
        headersTicketsExport.map(item => ({ value: item.value })),
        ...data.map(exportTicketRow),
    ]

    return (
        <Accordion defaultExpanded={defaultExpanded}>
            <AccordionSummary>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                    <span>{`${result.length <= 1 ? 'Ticket résolu' : 'Tickets résolus'} (${formatMilliers(result.length)} élément${result.length > 1 ? 's' : ''})`}</span>
                    {result.length != 0 && (
                        <Tooltip title='Exporter'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    ExportAction.exportData(formattingExportData(result), 'ResolvedIssues')
                                }}
                            >
                                <DownloadIcon fontSize='small' sx={{ color: 'white' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingX: '5px', paddingY: 0 }}>
                <NewTable
                    rows={formattingTickets(result)}
                    rowsPerPageDefault={10}
                    status={status}
                    WrapperComponent={Box}
                    headers={headersTicketsTable}
                    onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                />
            </AccordionDetails>
        </Accordion>
    )
}

IssuesResolvedCard.propTypes = {
    defaultExpanded: PropTypes.bool,
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        categories: PropTypes.arrayOf(
            PropTypes.oneOf(Object.values(CATEGORIES_TICKETS)),
        ),
        projects: PropTypes.arrayOf(
            PropTypes.string,
        ),
    }),
}

// Les tickets créés/stock exposent "tempspasse" (NewIssueInfo) ; on normalise vers "tempsPasse"
const formatCreatedRaw = rows => rows.map(row => ({ ...row, tempsPasse: row.tempspasse }))

const IssuesCreatedCard = ({
    filter,
    defaultExpanded,
}) => {
    const {
        data: result = [],
        reload: reload,
        status: status,
    } = useFetch(ApplicationConf.support.statisticsCreated(), { method: 'POST', body: filter }, { formatResult: formatCreatedRaw })

    useUpdateEffect(() => {
        reload()
    }, [filter])

    const formattingExportData = (data) => [
        headersTicketsExport.map(item => ({ value: item.value })),
        ...data.map(exportTicketRow),
    ]

    return (
        <Accordion defaultExpanded={defaultExpanded}>
            <AccordionSummary>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                    <span>{`${result.length <= 1 ? 'Ticket créé' : 'Tickets créés'} (${formatMilliers(result.length)} élément${result.length > 1 ? 's' : ''})`}</span>
                    {result.length != 0 && (
                        <Tooltip title='Exporter'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    ExportAction.exportData(formattingExportData(result), 'CreatedIssues')
                                }}
                            >
                                <DownloadIcon fontSize='small' sx={{ color: 'white' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingX: '5px', paddingY: 0 }}>
                <NewTable
                    rows={formattingTickets(result)}
                    rowsPerPageDefault={10}
                    status={status}
                    WrapperComponent={Box}
                    headers={headersTicketsTable}
                    onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                />
            </AccordionDetails>
        </Accordion>
    )
}

IssuesCreatedCard.propTypes = {
    defaultExpanded: PropTypes.bool,
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        categories: PropTypes.arrayOf(
            PropTypes.oneOf(Object.values(CATEGORIES_TICKETS)),
        ),
        projects: PropTypes.arrayOf(
            PropTypes.string,
        ),
    }),
}

const IssuesStockCard = ({
    filter,
    defaultExpanded,
}) => {
    const {
        data: result = [],
        reload: reload,
        status: status,
    } = useFetch(ApplicationConf.support.statisticsStock(), { method: 'POST', body: filter }, { formatResult: formatCreatedRaw })

    useUpdateEffect(() => {
        reload()
    }, [filter])

    const formattingExportData = (data) => [
        headersStockExport.map(item => ({ value: item.value })),
        ...data.map(exportStockRow),
    ]

    return (
        <Accordion defaultExpanded={defaultExpanded}>
            <AccordionSummary>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                    <span>{`Stock (${formatMilliers(result.length)} élément${result.length > 1 ? 's' : ''})`}</span>
                    {result.length != 0 && (
                        <Tooltip title='Exporter'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    ExportAction.exportData(formattingExportData(result), 'StockIssues')
                                }}
                            >
                                <DownloadIcon fontSize='small' sx={{ color: 'white' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingX: '5px', paddingY: 0 }}>
                <NewTable
                    rows={formattingTickets(result)}
                    rowsPerPageDefault={10}
                    status={status}
                    WrapperComponent={Box}
                    headers={headersStockTable}
                    onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                />
            </AccordionDetails>
        </Accordion>
    )
}

IssuesStockCard.propTypes = {
    defaultExpanded: PropTypes.bool,
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        categories: PropTypes.arrayOf(
            PropTypes.oneOf(Object.values(CATEGORIES_TICKETS)),
        ),
        projects: PropTypes.arrayOf(
            PropTypes.string,
        ),
    }),
}

// Rapport mensuel Dev : respect des échéances de lots (custom field Mantis "lot").
const statutLot = (row) => {
    const enRetard = (row.retardJours ?? 0) > 0
    if (!row.termine) {
        return enRetard ? 'En retard' : 'En cours'
    }
    // Lot terminé mais sans échéance : pas de date à dépasser -> non évaluable.
    if (!row.echeance) {
        return 'Terminé'
    }
    return row.respecte ? 'Respecté' : 'Terminé en retard'
}

// Rouge = urgent (en cours + dépassé) ; orange = terminé mais livré en retard ; vert = respecté.
const statutColor = (statut) => {
    switch (statut) {
        case 'En retard': return 'red'
        case 'Terminé en retard': return '#e65100'
        case 'Respecté': return 'green'
        default: return undefined
    }
}

// Barre d'avancement (0-100 %), même rendu que le titre des accordéons de DevsApp.
const AvancementBar = ({ value = 0, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120, pr: 3 }}>
        <LinearProgress
            variant='determinate'
            value={value}
            sx={{
                flexGrow: 1,
                height: '16px',
                borderRadius: '5px',
                [`&.${linearProgressClasses.colorPrimary}`]: { backgroundColor: '#eee' },
                [`& .${linearProgressClasses.bar}`]: { backgroundColor: color ?? getColorAdv(value) },
            }}
        />
        <Typography variant='body2'>{`${value}%`}</Typography>
    </Box>
)

AvancementBar.propTypes = {
    value: PropTypes.number,
    color: PropTypes.string,
}

const formattingLotsTable = (lots, excludedLots) => lots.map(row => {
    const statut = statutLot(row)
    const color = statutColor(statut)
    const enRetard = (row.retardJours ?? 0) > 0
    const excluded = excludedLots.has(row.lot)
    const muted = excluded ? '#bbb' : undefined
    return {
        id: row.lot,
        lot: { value: row.lot, fontColor: muted },
        nbTickets: { value: formatMilliers(row.nbTickets), sortValue: row.nbTickets, fontColor: muted },
        nbOuverts: { value: formatMilliers(row.nbOuverts), sortValue: row.nbOuverts, fontColor: muted },
        avancement: { value: <AvancementBar value={row.avancement ?? 0} color={excluded ? '#ccc' : ((!row.termine && enRetard) ? 'red' : undefined)} />, sortValue: row.avancement ?? 0 },
        dateDebut: { value: row.dateDebut ? getDate(row.dateDebut * 1000) : '', sortValue: row.dateDebut ?? -1, fontColor: muted },
        echeance: { value: row.echeance ? getDate(row.echeance * 1000) : '', sortValue: row.echeance ?? -1, fontColor: muted },
        dateFin: (() => {
            const done = row.termine && row.dateFin
            const dateStr = done ? getDate(row.dateFin * 1000) : ''
            const value = (done && row.dernierTicketFin)
                ? <Tooltip title={`Dernier ticket validé : #${row.dernierTicketFin}`}><span>{dateStr}</span></Tooltip>
                : dateStr
            return { value, sortValue: done ? row.dateFin : -1, fontColor: muted }
        })(),
        duree: (() => {
            const done = row.termine && row.dateFin && row.dateDebut
            const jours = done ? Math.round((row.dateFin - row.dateDebut) / 86400) : null
            return { value: done ? `${formatMilliers(jours)} j` : '', sortValue: done ? jours : -1, fontColor: muted }
        })(),
        statut: { value: statut, fontColor: excluded ? '#bbb' : color },
        retard: (() => {
            const tickets = row.ticketsRetard ?? []
            const label = enRetard ? `${formatMilliers(row.retardJours, true)} j` : ''
            // Fin retenue pour le calcul : la plus proche entre la fin du ticket et la clôture du lot ; absente = ticket pas encore terminé.
            const ticketLine = (t) => `#${t.id} : ${formatMilliers(t.retard, true)} j (${t.dateFin ? `fin le ${getDate(t.dateFin * 1000)}` : 'en cours'})`
            const title = ['Tickets en retard :', ...tickets.map(ticketLine)].join('\n')
            const value = (enRetard && tickets.length > 0)
                ? <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{title}</span>}><span>{label}</span></Tooltip>
                : label
            return { value, sortValue: row.retardJours ?? -1, fontColor: excluded ? '#bbb' : color }
        })(),
        tauxRetard: (() => {
            const done = row.termine && row.dateFin && row.dateDebut
            const dureeJours = done ? (row.dateFin - row.dateDebut) / 86400 : null
            const taux = (done && dureeJours > 0) ? round((row.retardJours ?? 0) / dureeJours * 100, 1) : null
            return { value: taux !== null ? `${formatMilliers(taux, true)} %` : '', sortValue: taux ?? -1, fontColor: excluded ? '#bbb' : color }
        })(),
    }
})

const headersLotsTable = [{ key: 'lot', value: 'Lot' }, { key: 'nbTickets', value: 'Nb tickets' },
    { key: 'nbOuverts', value: 'Tickets ouverts' },
    { key: 'avancement', value: 'Avancement', tooltip: 'Moyenne des avancements des tickets du lot (un ticket sans avancement compte pour 0).' },
    { key: 'dateDebut', value: 'Date de début', tooltip: 'Date d\'ouverture du lot (1er passage du champ « Lot » en « 1.x »).' },
    { key: 'echeance', value: 'Échéance', tooltip: 'Date d\'échéance la plus précoce renseignée parmi les tickets.' },
    { key: 'dateFin', value: 'Date de fin', tooltip: <span style={{ whiteSpace: 'pre-line' }}>{'Date à laquelle le dernier ticket du lot est passé en « Attente de validation » (statut ≥ 60).\nAffichée seulement quand tous les tickets sont terminés.'}</span> },
    { key: 'duree', value: 'Durée', tooltip: 'Durée du lot terminé : écart entre la date de début et la date de fin. Vide pour les lots en cours.' },
    { key: 'statut', value: 'Statut' },
    { key: 'retard', value: 'Retard', tooltip: <span style={{ whiteSpace: 'pre-line' }}>{'Retard moyen calculé ticket par ticket puis moyenné.\nPour chaque ticket : écart entre sa date de fin (ou aujourd\'hui s\'il est encore ouvert) et son échéance.\nUn ticket sans échéance prend l\'échéance la plus faible du lot.'}</span> },
    { key: 'tauxRetard', value: 'Taux de retard', tooltip: 'Retard du lot rapporté à sa durée (retard / durée). Vide pour les lots en cours.' }]

const SyntheseCard = ({ label, value, sub }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Typography variant='h4'>{value}</Typography>
            <Typography variant='body2' color='textSecondary'>{label}</Typography>
            {sub && <Typography variant='caption' color='textSecondary'>{sub}</Typography>}
        </CardContent>
    </Card>
)

SyntheseCard.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sub: PropTypes.string,
}

const LotsReportCard = ({
    filter,
}) => {
    // Prise en compte des tickets du projet IAEAU dans le périmètre des lots (un lot peut être
    // multi-projets) : coché par défaut, sinon les lots mêlant IAEAU apparaissent à tort terminés.
    const [includeIaeau, setIncludeIaeau] = useState(true)

    // Lots exclus manuellement des stats (par leur valeur de lot). Purement client : ils restent
    // affichés (grisés) dans le tableau mais ne comptent plus dans les cartes de synthèse.
    const [excludedLots, setExcludedLots] = useState(() => new Set())

    // Dialog d'évolution mensuelle des 5 KPI (un appel statisticsLots par mois de la plage).
    const [evolutionOpen, setEvolutionOpen] = useState(false)

    const body = useMemo(() => ({ ...filter, includeIaeau }), [filter, includeIaeau])

    const {
        data = { lots: [], synthese: {} },
        reload: reload,
        status: status,
    } = useFetch(ApplicationConf.support.statisticsLots(), { method: 'POST', body })

    // Tout changement de périmètre (filtre ou IAEAU) repart d'une sélection sans exclusion.
    useUpdateEffect(() => {
        reload()
        setExcludedLots(new Set())
    }, [body])

    const toggleExcludeLot = (lot) => setExcludedLots((prev) => {
        const next = new Set(prev)
        if (next.has(lot)) {
            next.delete(lot)
        } else {
            next.add(lot)
        }
        return next
    })

    const lots = data?.lots ?? []
    const synthese = useMemo(() => computeSynthese(lots.filter(l => !excludedLots.has(l.lot))), [lots, excludedLots])

    const formattingExportData = (rows) => {
        const headers = headersLotsTable.map(item => ({ value: item.value }))
        const res = rows.map(row => ([
            { value: row.lot ?? '' },
            { value: String(row.nbTickets ?? '') },
            { value: String(row.nbOuverts ?? '') },
            { value: `${row.avancement ?? 0}%` },
            { value: row.dateDebut ? getDate(row.dateDebut * 1000) : '' },
            { value: row.echeance ? getDate(row.echeance * 1000) : '' },
            { value: (row.termine && row.dateFin) ? getDate(row.dateFin * 1000) : '' },
            { value: (row.termine && row.dateFin && row.dateDebut) ? `${Math.round((row.dateFin - row.dateDebut) / 86400)} j` : '' },
            { value: statutLot(row) },
            { value: (row.retardJours ?? 0) > 0 ? String(row.retardJours) : '' },
            { value: (() => {
                const dureeJours = (row.termine && row.dateFin && row.dateDebut) ? (row.dateFin - row.dateDebut) / 86400 : null
                return (dureeJours > 0) ? `${round((row.retardJours ?? 0) / dureeJours * 100, 1)} %` : ''
            })() },
        ]))
        return [headers, ...res]
    }

    return (
        <Stack spacing={2}>
            <Box display='flex' justifyContent='flex-end'>
                <Button
                    variant='outlined'
                    startIcon={<TimelineIcon />}
                    onClick={() => setEvolutionOpen(true)}
                    disabled={lots.length === 0}
                >
                    {'Voir l\'évolution'}
                </Button>
            </Box>
            <Grid container spacing={2}>
                <Grid size={2.4}><SyntheseCard label='Lots' value={formatMilliers(synthese.nbLots ?? 0)} /></Grid>
                <Grid size={2.4}><SyntheseCard label='Lots terminés' value={formatMilliers(synthese.nbTermines ?? 0)} sub={`dont ${formatMilliers(synthese.nbEvaluables ?? 0)} avec échéance`} /></Grid>
                <Grid size={2.4}><SyntheseCard label='% respectés' value={`${synthese.tauxRespect ?? 0} %`} sub={`${formatMilliers(synthese.nbRespectes ?? 0)}/${formatMilliers(synthese.nbEvaluables ?? 0)} évaluables`} /></Grid>
                <Grid size={2.4}><SyntheseCard label='Retard moyen (jours)' value={formatMilliers(synthese.retardMoyen ?? 0)} sub={`sur ${formatMilliers(synthese.nbEnRetard ?? 0)} lot(s) en retard`} /></Grid>
                <Grid size={2.4}><SyntheseCard label='Taux de retard moyen' value={`${synthese.tauxRetardMoyen ?? 0} %`} sub={`sur ${formatMilliers(synthese.nbTauxRetard ?? 0)} lot(s) en retard`} /></Grid>
            </Grid>
            <CardTable
                title='Respect des échéances de lots'
                rows={formattingLotsTable(lots, excludedLots)}
                actions={[{
                    icon: 'api',
                    tooltip: includeIaeau ? 'Tickets IAEAU inclus (cliquer pour exclure)' : 'Tickets IAEAU exclus (cliquer pour inclure)',
                    color: includeIaeau ? '#4fc3f7' : '#9e9e9e',
                    onClick: () => setIncludeIaeau(v => !v),
                }, {
                    icon: 'download',
                    tooltip: 'Exporter',
                    displayed: lots.length != 0,
                    onClick: () => {
                        const exportData = formattingExportData(lots)
                        ExportAction.exportData(exportData, 'LotsReport')
                    },
                }]}
                lineActions={[{
                    icon: 'block',
                    // tooltip: 'Exclure / réintégrer des stats',
                    align: ALIGN.RIGHT,
                    onClick: ({ id }) => toggleExcludeLot(id),
                }]}
                color='#161832'
                rowsPerPageDefault={-1}
                status={status}
                headers={headersLotsTable}
                onClickRow={({ id }) => window.open(`/devs?lot=${encodeURIComponent(id)}`, '_blank')}
                defaultSort={{ column: 'statut', direction: SORT.ASC }}
            />
            <LotsEvolutionDialog
                open={evolutionOpen}
                onClose={() => setEvolutionOpen(false)}
                filter={filter}
                includeIaeau={includeIaeau}
                excludedLots={excludedLots}
            />
        </Stack>
    )
}

LotsReportCard.propTypes = {
    filter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
    }),
}

const TICKETS = 'TICKETS'
const LOTS = 'LOTS'
const INCIDENTS = 'INCIDENTS'

const StatisticsSupportApp = () => {
    useTitles(() => [{
        label: 'Statistiques',
        href: '/statistics',
    }], [])

    const defaultFilter = {
        startDate: moment().subtract(1, 'year').startOf('month').valueOf(),
        endDate: moment().valueOf(),
        categories: ['Anomalie'],
        projects: ['V7-Web'],
    }

    const [filter, setFilter] = useState(defaultFilter)
    // Popin d'évolution mensuelle du support (6 indicateurs, cf. SupportEvolutionDialog).
    const [evolutionOpen, setEvolutionOpen] = useState(false)

    const body = useMemo(() => {
        return {
            startDate: round(filter.startDate / 1000),
            endDate: round(filter.endDate / 1000),
            projects: filter.projects,
            categories: filter.categories,
        }
    }, [filter])

    const {
        data: graphData = [],
        reload: reloadGraph,
        status: graphStatus,
    } = useFetch(ApplicationConf.support.statisticsGraph(), { method: 'POST', body })

    useUpdateEffect(() => {
        reloadGraph()
    }, [body])

    const graphLoading = graphStatus === STATUS.LOADING

    return (
        <Box>
            <TabList
                tabs={[{
                    constant: TICKETS,
                    label: 'Tickets',
                    icon: 'bug_report',
                }, {
                    constant: LOTS,
                    label: 'Lots',
                    icon: 'logo_dev',
                }, {
                    constant: INCIDENTS,
                    label: 'Incidents',
                    icon: 'priority_high',
                }]}
                headerComponent={(
                    <FilterStatistics
                        defaultFilter={defaultFilter}
                        setFilter={setFilter}
                    />
                )}
            >
                {t => {
                    if (t === TICKETS) {
                        return (
                            <Stack margin={1} spacing={2} container >
                                <Box display='flex' justifyContent='flex-end'>
                                    <Button
                                        variant='outlined'
                                        startIcon={<TimelineIcon />}
                                        onClick={() => setEvolutionOpen(true)}
                                    >
                                        {'Voir l\'évolution'}
                                    </Button>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid size={6}>
                                        <TimesBar chartData={graphData} loading={graphLoading} />
                                    </Grid>
                                    <Grid size={6}>
                                        <TicketsLine chartData={graphData} loading={graphLoading} />
                                    </Grid>
                                </Grid>
                                <IssuesStockCard filter={body} defaultExpanded />
                                <IssuesResolvedCard filter={body} />
                                <IssuesCreatedCard filter={body} />
                                <SupportEvolutionDialog
                                    open={evolutionOpen}
                                    onClose={() => setEvolutionOpen(false)}
                                    filter={body}
                                />
                            </Stack>
                        )
                    }
                    if (t === LOTS) {
                        return (
                            <LotsReportCard
                                filter={body}
                            />
                        )
                    }
                    if (t === INCIDENTS) {
                        return (
                            <IncidentsSupportApp
                                filter={body}
                            />
                        )
                    }
                }}
            </TabList>
        </Box>
    )
}

export default StatisticsSupportApp